import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { StickToBottom } from "../../../../agent-ui";
import { ChatUserMessageComponent } from "../chat-user-message/chat-user-message.component";
import { ChatAssistantMessageComponent } from "../chat-assistant-message/chat-assistant-message.component";
import { ChatIdleStateComponent } from "../chat-idle-state/chat-idle-state.component";
import { ReasoningStepComponent } from "../chat-assistant-message/reasoning-step/reasoning-step.component";
import { TodoListCardComponent } from "../content/cards/todo-list-card/todo-list-card.component";
import { PlanProgressCardComponent } from "../content/cards/plan-progress-card/plan-progress-card.component";
import { MessageClassifierService } from "../../services/message-classifier.service";
import type { MessageItem, ResolvedToolCall } from "../../services/message-classifier.service";
import { WorkerReasoningStreamService } from "../../services/worker-reasoning-stream.service";
import { WorkerTodoStreamService } from "../../services/worker-todo-stream.service";
import { BlackboardStreamService } from "../../services/blackboard-stream.service";
import { EvidenceProgressStore } from "../../services/evidence-progress-store.service";
import { EvidenceProgressCardComponent } from "../content/cards/evidence-progress-card/evidence-progress-card.component";
import { DebugModeService } from "../../services/debug-mode.service";
import { isSubAgentTodoListDone } from "../../model/block.types";

export type { MessageItem, ResolvedToolCall };

/**
 * Scrollable message list with auto-scroll via StickToBottom.
 *
 * Classification is done purely from the AG-UI message content (no state
 * snapshot dependency), so it works during streaming as chunks arrive.
 *
 * Reasoning steps are only shown for the current turn — once a normal
 * assistant message appears after them, they collapse. A live sub-agent
 * todo checklist (TodoListCardComponent), if any, collapses on its own once
 * every todo is completed.
 */
@Component({
  selector: "lib-chat-message-list",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StickToBottom,
    ChatUserMessageComponent,
    ChatAssistantMessageComponent,
    ChatIdleStateComponent,
    ReasoningStepComponent,
    TodoListCardComponent,
    PlanProgressCardComponent,
    EvidenceProgressCardComponent,
  ],
  templateUrl: "./chat-message-list.component.html",
  styleUrl: "./chat-message-list.component.scss",
})
export class ChatMessageListComponent {
  readonly messages = input<any[]>([]);

  readonly showCursor = input<boolean>(false);

  private readonly messageClassifier = inject(MessageClassifierService);

  // Optional: only present under a chat-container that provides it. Live sub-agent
  // (reasoning) steps are woven into the current turn below.
  private readonly workerReasoning = inject(WorkerReasoningStreamService, { optional: true });

  // Optional: same lifecycle as workerReasoning. Live *_todo_update checklist for a
  // sub-agent running as part of the main turn (no background_job wrapper) — woven in
  // as a persistent TodoListCardComponent alongside reasoning steps.
  private readonly workerTodo = inject(WorkerTodoStreamService, { optional: true });

  // Optional: same lifecycle as workerReasoning/workerTodo. Live planning-pipeline progress
  // from blackboard_snapshot custom events — woven in as a PlanProgressCardComponent (which
  // reads the same container-scoped BlackboardStreamService itself).
  private readonly blackboard = inject(BlackboardStreamService, { optional: true });

  // Optional: same lifecycle as the blackboard stream. Ephemeral evidence_progress brackets
  // for the DEBUG-ONLY evidence card family — the card reads the same container-scoped
  // EvidenceProgressStore itself; the weave only decides whether the element exists at all.
  private readonly evidenceProgress = inject(EvidenceProgressStore, { optional: true });

  // Debug gate for the evidence weave: the card is debug-only always (user-approved scope).
  private readonly debugMode = inject(DebugModeService);

  /**
   * Whether the evidence progress card should exist in this list: a container-scoped store is
   * present, debug mode is on, and the store has at least one visible bracket view. The card
   * auto-guards itself with the same triple condition, so both layers must agree.
   */
  protected readonly showEvidence = computed<boolean>(() => {
    if (!this.evidenceProgress || !this.debugMode.isDebugMode()) return false;
    return this.evidenceProgress.view().length > 0;
  });

  protected readonly items = computed<MessageItem[]>(() => {
    const items = this.messageClassifier.classify(this.messages() as any[]);

    // Weave in live sub-agent reasoning (reasoning custom events) and the todo checklist
    // (*_todo_update/*_tool_call custom events) right after the last user message (start of
    // the current turn), in that order — reasoning ("Styling map layer" progress) on top,
    // the todo checklist card below it, so the last progress line always sits directly above
    // the checklist instead of the two being interleaved by insertion order.
    const toWeave: MessageItem[] = [];

    // NOT messages — they come from WorkerReasoningStreamService and stream token-by-token
    // (status="in_progress" → "completed"). Collapse logic below hides them once the final
    // assistant answer arrives, like every other step.
    const workerSteps = this.workerReasoning?.steps() ?? [];
    for (const s of workerSteps) {
      toWeave.push({
        kind: "reasoning",
        id: "wr:" + s.key,
        stepInfo: { stepLabel: s.stepLabel, description: s.reasoning },
      });
    }

    // Rendered as a card. Collapses (stops being woven in at all) once every todo is
    // completed — a finished checklist has served its purpose, same as a reasoning step
    // collapsing once the final answer lands, just triggered by isSubAgentTodoListDone
    // instead of "an assistant message followed it".
    const todos = this.workerTodo?.todos() ?? [];
    if (todos.length && !isSubAgentTodoListDone(todos)) {
      toWeave.push({
        kind: "todo_list",
        id: "wr:todo_list",
        todos,
        toolCall: this.workerTodo?.toolCall() ?? null,
        label: this.workerTodo?.label() ?? null,
      });
    }

    // Rendered as a card covering the whole planning-pipeline lifecycle: phase indicators
    // per the doc 31 8-phase catalog and per-task rows from the live engine run. Woven in only
    // while there's something to show — an empty/never-started blackboard has nothing worth a
    // card for.
    const indicators = this.blackboard?.indicators() ?? {};
    const taskRows = [...(this.blackboard?.taskRows()?.values() ?? [])];
    if (Object.keys(indicators).length || taskRows.length) {
      toWeave.push({ kind: "blackboard", id: "wr:blackboard", indicators, taskRows });
    }

    if (toWeave.length) {
      const lastUserIdx = items.reduce((acc, item, i) => (item.kind === "user" ? i : acc), -1);
      if (lastUserIdx === -1) items.push(...toWeave);
      else items.splice(lastUserIdx + 1, 0, ...toWeave);
    }

    // Collapse reasoning steps that already have a final assistant answer after them
    const lastAssistantIdx = items.reduce((acc, item, i) => (item.kind === "assistant" ? i : acc), -1);
    if (lastAssistantIdx === -1) return items;
    return items.filter((item, i) => item.kind !== "reasoning" || i > lastAssistantIdx);
  });

  protected readonly showAnalyzing = computed(() => {
    if (!this.showCursor()) return false;
    const list = this.items();
    if (list.length === 0) return true;
    const last = list[list.length - 1];
    return last.kind === "pending" || last.kind === "user";
  });

  protected readonly analyzingStepInfo = { stepLabel: "Analyzing request…", description: "" };

  protected trackItem(_index: number, item: MessageItem): string {
    switch (item.kind) {
      case "user":
      case "assistant":
        return item.message.id;
      case "reasoning":
      case "todo_list":
      case "blackboard":
        return item.id;
      case "pending":
        return "__pending__" + _index;
    }
  }
}
